import { useCallback } from "react";
import useSWR from "swr";
import { NestedPick, Paginated, PaginationQueryWithFields } from "../../types";
import { buildUrl, useApi } from "../api";
import { CustomError } from "../errors";
import { WorkflowAttachment, WorkflowAttachmentFile } from "./types";

export const useMyCarWorkflowAttachmentsApi = () => {
  const api = useApi();

  const insertWorkflowAttachments = useCallback(
    async (files: File[], workflowId: string, carId?: string): Promise<void> => {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append("files", file);
      });

      const url = `/workflows/${workflowId}/attachments`;
      await api.put<FormData>(url, formData);
      api.mutateCache([
        url,
        `/admin/workflows/${workflowId}/attachments`,
        `/admin/workflow_attachments`,
        `/admin/cars/${carId}/workflows`,
        `/admin/workflows`,
        `/my_cars/${carId}/workflows`,
      ]);
    },
    [api],
  );

  const useWorkflowAttachments = useCallback(
    <K extends keyof WorkflowAttachment>(
      query: PaginationQueryWithFields<NestedPick<WorkflowAttachment, K>>,
      signal: AbortSignal,
      workflowId?: string,
    ) => {
      const url = buildUrl<NestedPick<WorkflowAttachment, K>>(`/workflows/${workflowId}/attachments`, query);
      return useSWR<Paginated<NestedPick<WorkflowAttachment, K>> | null, CustomError, string>(url, (url) =>
        api.get<Paginated<NestedPick<WorkflowAttachment, K>>>({ url, signal }),
      );
    },
    [api],
  );

  return {
    insertWorkflowAttachments,
    useWorkflowAttachments,
  };
};

export const useWorkflowAttachmentsApi = () => {
  const api = useApi();

  const useWorkflowAttachments = useCallback(
    (signal: AbortSignal, workflowId?: string) => {
      const url = workflowId ? `/admin/workflows/${workflowId}/attachments` : "/admin/workflow_attachments";
      return useSWR<Paginated<WorkflowAttachmentFile>>(url, () =>
        api.get<Paginated<WorkflowAttachmentFile>>({ url, signal }),
      );
    },
    [api],
  );

  return {
    useWorkflowAttachments,
  };
};
